import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import {
  ServiceProviderProfileImage,
  ServiceProviderProfileImageMutation,
  ServiceProviderProfileImageVariantInput,
  ServiceProviderProfileImageVariantName,
} from '../types/firestore';
import { storage } from '../firebase';

const SUPPORTED_INPUT_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const PROFILE_IMAGE_MAX_INPUT_BYTES = 10 * 1024 * 1024;
const PROFILE_IMAGE_FULL_EDGE = 1024;
const PROFILE_IMAGE_THUMB_EDGE = 320;
const PROFILE_IMAGE_FULL_MAX_BYTES = 900 * 1024;
const PROFILE_IMAGE_THUMB_MAX_BYTES = 180 * 1024;
const PROFILE_IMAGE_OUTPUT_MIME_TYPE = 'image/jpeg';
type UploadedServiceProviderProfileImageVariant = ServiceProviderProfileImage['full'];

export interface ServiceProviderProfileImageDraft {
  assetId: string;
  sourceFileName: string | null;
  sourceMimeType: string;
  sourceBytes: number;
  sourceWidth: number;
  sourceHeight: number;
  full: ServiceProviderProfileImageVariantInput & { blob: Blob };
  thumb: ServiceProviderProfileImageVariantInput & { blob: Blob };
  previewUrl: string;
}

const createAssetId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildStoragePath = (
  uid: string,
  assetId: string,
  variant: ServiceProviderProfileImageVariantName
): string => `service-providers/${uid}/profile-images/${assetId}/${variant}.jpg`;

const loadImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Nie udało się odczytać obrazu.'));
    };
    image.src = objectUrl;
  });

const createSquareCanvas = (
  image: HTMLImageElement,
  outputSize: number
): {
  canvas: HTMLCanvasElement;
  cropSize: number;
  sourceWidth: number;
  sourceHeight: number;
} => {
  const canvas = document.createElement('canvas');
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const cropSize = Math.max(1, Math.min(sourceWidth, sourceHeight));
  const nextSize = Math.max(1, Math.min(outputSize, cropSize));
  const offsetX = Math.max(0, Math.floor((sourceWidth - cropSize) / 2));
  const offsetY = Math.max(0, Math.floor((sourceHeight - cropSize) / 2));

  canvas.width = nextSize;
  canvas.height = nextSize;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Przeglądarka nie wspiera renderowania obrazu.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, nextSize, nextSize);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, offsetX, offsetY, cropSize, cropSize, 0, 0, nextSize, nextSize);

  return {
    canvas,
    cropSize,
    sourceWidth,
    sourceHeight,
  };
};

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Nie udało się przygotować obrazu.'));
          return;
        }
        resolve(blob);
      },
      PROFILE_IMAGE_OUTPUT_MIME_TYPE,
      quality
    );
  });

const renderCanvasWithSizeBudget = async (
  canvas: HTMLCanvasElement,
  maxBytes: number,
  initialQuality = 0.9
): Promise<Blob> => {
  const qualitySteps = [initialQuality, 0.84, 0.78, 0.72, 0.66, 0.6];
  let lastBlob: Blob | null = null;

  for (const quality of qualitySteps) {
    const candidate = await canvasToBlob(canvas, quality);
    lastBlob = candidate;
    if (candidate.size <= maxBytes) {
      return candidate;
    }
  }

  if (!lastBlob) {
    throw new Error('Nie udało się skompresować obrazu.');
  }

  if (lastBlob.size > maxBytes) {
    throw new Error('Nie udało się zmniejszyć obrazu do wymaganego rozmiaru.');
  }

  return lastBlob;
};

export const validateServiceProviderProfileImageFile = (file: File): string | null => {
  if (!file.type || !SUPPORTED_INPUT_MIME_TYPES.has(file.type)) {
    return 'Obsługiwane formaty to JPG, PNG i WebP.';
  }

  if (file.size > PROFILE_IMAGE_MAX_INPUT_BYTES) {
    return 'Plik jest zbyt duży. Maksymalny rozmiar to 10 MB.';
  }

  return null;
};

export const buildServiceProviderProfileImageStoragePath = (
  uid: string,
  assetId: string,
  variant: ServiceProviderProfileImageVariantName
): string => buildStoragePath(uid, assetId, variant);

export const prepareServiceProviderProfileImageDraft = async (
  file: File
): Promise<ServiceProviderProfileImageDraft> => {
  const validationError = validateServiceProviderProfileImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const image = await loadImageElement(file);
  const assetId = createAssetId();

  const fullCanvas = createSquareCanvas(image, PROFILE_IMAGE_FULL_EDGE);
  const thumbCanvas = createSquareCanvas(image, PROFILE_IMAGE_THUMB_EDGE);
  const fullBlob = await renderCanvasWithSizeBudget(
    fullCanvas.canvas,
    PROFILE_IMAGE_FULL_MAX_BYTES
  );
  const thumbBlob = await renderCanvasWithSizeBudget(
    thumbCanvas.canvas,
    PROFILE_IMAGE_THUMB_MAX_BYTES
  );

  const previewUrl = URL.createObjectURL(fullBlob);
  const sourceFileName = file.name ? file.name.trim() : null;

  return {
    assetId,
    sourceFileName: sourceFileName && sourceFileName.length > 0 ? sourceFileName : null,
    sourceMimeType: file.type,
    sourceBytes: file.size,
    sourceWidth: fullCanvas.sourceWidth,
    sourceHeight: fullCanvas.sourceHeight,
    full: {
      blob: fullBlob,
      storagePath: '',
      contentType: PROFILE_IMAGE_OUTPUT_MIME_TYPE,
      bytes: fullBlob.size,
      width: fullCanvas.canvas.width,
      height: fullCanvas.canvas.height,
    },
    thumb: {
      blob: thumbBlob,
      storagePath: '',
      contentType: PROFILE_IMAGE_OUTPUT_MIME_TYPE,
      bytes: thumbBlob.size,
      width: thumbCanvas.canvas.width,
      height: thumbCanvas.canvas.height,
    },
    previewUrl,
  };
};

const uploadVariant = async (
  uid: string,
  assetId: string,
  variant: ServiceProviderProfileImageVariantName,
  variantPayload: ServiceProviderProfileImageDraft['full'],
  totalBytes: number,
  alreadyUploadedBytes: number,
  onProgress?: (progress: number) => void
): Promise<UploadedServiceProviderProfileImageVariant> => {
  const storagePath = buildStoragePath(uid, assetId, variant);
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, variantPayload.blob, {
    contentType: variantPayload.contentType,
  });

  await new Promise<void>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const bytesTransferred = alreadyUploadedBytes + snapshot.bytesTransferred;
        const progress = totalBytes > 0 ? Math.round((bytesTransferred / totalBytes) * 100) : 0;
        onProgress?.(Math.min(progress, 99));
      },
      reject,
      () => resolve()
    );
  });

  const url = await getDownloadURL(uploadTask.snapshot.ref);

  return {
    url,
    storagePath,
    contentType: variantPayload.contentType,
    bytes: variantPayload.bytes,
    width: variantPayload.width,
    height: variantPayload.height,
  };
};

export const uploadServiceProviderProfileImageDraft = async (
  uid: string,
  draft: ServiceProviderProfileImageDraft,
  onProgress?: (progress: number) => void
): Promise<ServiceProviderProfileImage> => {
  const totalBytes = draft.full.bytes + draft.thumb.bytes;
  const uploadedPaths: string[] = [];

  try {
    const full = await uploadVariant(
      uid,
      draft.assetId,
      'full',
      draft.full,
      totalBytes,
      0,
      onProgress
    );
    uploadedPaths.push(full.storagePath);

    const thumb = await uploadVariant(
      uid,
      draft.assetId,
      'thumb',
      draft.thumb,
      totalBytes,
      draft.full.bytes,
      onProgress
    );
    uploadedPaths.push(thumb.storagePath);

    onProgress?.(100);

    return {
      assetId: draft.assetId,
      sourceFileName: draft.sourceFileName,
      sourceMimeType: draft.sourceMimeType,
      sourceBytes: draft.sourceBytes,
      sourceWidth: draft.sourceWidth,
      sourceHeight: draft.sourceHeight,
      createdAt: null,
      full,
      thumb,
    };
  } catch (error) {
    await Promise.all(
      uploadedPaths.map(async (storagePath) => {
        try {
          await deleteObject(ref(storage, storagePath));
        } catch {
          // Best effort cleanup only.
        }
      })
    );
    throw error;
  }
};

export const deleteServiceProviderProfileImageUpload = async (
  image: Pick<ServiceProviderProfileImage, 'full' | 'thumb'> | null | undefined
): Promise<void> => {
  if (!image) return;

  await Promise.all(
    [image.full?.storagePath, image.thumb?.storagePath]
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
      .map(async (storagePath) => {
        try {
          await deleteObject(ref(storage, storagePath));
        } catch {
          // Best effort cleanup only.
        }
      })
  );
};

export const toServiceProviderProfileImageMutation = (
  image: ServiceProviderProfileImage
): ServiceProviderProfileImageMutation => ({
  assetId: image.assetId,
  sourceFileName: image.sourceFileName,
  sourceMimeType: image.sourceMimeType,
  sourceBytes: image.sourceBytes,
  sourceWidth: image.sourceWidth,
  sourceHeight: image.sourceHeight,
  full: {
    storagePath: image.full.storagePath,
    contentType: image.full.contentType,
    bytes: image.full.bytes,
    width: image.full.width,
    height: image.full.height,
  },
  thumb: {
    storagePath: image.thumb.storagePath,
    contentType: image.thumb.contentType,
    bytes: image.thumb.bytes,
    width: image.thumb.width,
    height: image.thumb.height,
  },
});
